import React, { useState, useEffect } from "react";
import { Spinner, Button } from "flowbite-react";
import { API_BASE_URL } from "../../config";
import ImageCropper from "../ImageCropper.js";
import { createImage } from "../imageUtils.js";
import getCroppedImg from "../cropImage.js";

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [newDescription, setNewDescription] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [imagePreview, setImagePreview] = useState({});
  
useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, products]);

useEffect(() => {
  console.log("🚀 Estado actualizado en React:", products);
}, [products]);


  const ensureAbsoluteUrl = (path) =>
    path?.startsWith("http") ? path : `${API_BASE_URL}/${path}`;

// Define la función buildImageUrl al principio del archivo
const buildImageUrl = (imagePath, cliente_id) => {
  if (!imagePath) {
    // Si no hay imagePath, usa una imagen predeterminada
    return `${API_BASE_URL}/static/cliente_${cliente_id}/productos/default.jpg`;
  }

  // Si imagePath ya es una URL absoluta, devuélvela tal cual
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // Si es una ruta relativa, construye la URL absoluta
  return `${API_BASE_URL}/static/${imagePath}`;
};

const fetchProducts = async () => {
  const cliente_id = localStorage.getItem("cliente_id");
  if (!cliente_id) {
    console.error("cliente_id no está definido");
    return;
  }

  try {
    setLoading(true);

    const response = await fetch(`${API_BASE_URL}/productos?cliente_id=${cliente_id}`);
    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const data = await response.json();
    const productos = Array.isArray(data) ? data : data.productos;

    if (!Array.isArray(productos)) {
      throw new Error("La respuesta de productos no contiene un array válido.");
    }

    const updatedProducts = productos.map((product) => ({
      ...product,
      image_path: buildImageUrl(product.image_path, cliente_id),
      description: product.description || "",
    }));

    console.log("🚀 Estado actualizado en React:", updatedProducts);
    setProducts(updatedProducts);
    setFilteredProducts(updatedProducts);
  } catch (error) {
    console.error("Error al obtener productos:", error);
    setMessage("Error al actualizar productos.");
  } finally {
    setLoading(false);
  }
};

const updateDescription = async (productId) => {
  try {
    const cliente_id = localStorage.getItem("cliente_id");
    if (!cliente_id) throw new Error("No se pudo obtener el cliente_id.");

    const formattedClienteId = parseInt(cliente_id, 10);
    if (isNaN(formattedClienteId)) throw new Error("El cliente_id no es un número válido.");

    if (!newDescription || newDescription.trim().length === 0) {
      throw new Error("La descripción no puede estar vacía.");
    }

    const payload = { cliente_id: formattedClienteId, description: newDescription.trim() };

    console.log("Enviando datos a la API:", payload);

    const response = await fetch(`${API_BASE_URL}/productos/${productId}/descripcion`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error HTTP: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log("Respuesta de la API:", result);

    if (result.error) {
      throw new Error(result.error);
    }

    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId ? { ...product, description: newDescription.trim() } : product
      )
    );

    setFilteredProducts((prev) =>
      prev.map((product) =>
        product.id === productId ? { ...product, description: newDescription.trim() } : product
      )
    );

    setMessage("Descripción actualizada correctamente.");
    setEditingProductId(null);
    setNewDescription("");

  } catch (err) {
    console.error("Error al actualizar descripción:", err);
    setMessage(`Error: ${err.message}`);
  }
};

const handleCroppedImage = async (croppedBlob) => {
  setCropModalOpen(false);

  const file = new File([croppedBlob], "cropped.jpg", { type: "image/jpeg" });

  // 🔥 Muestra la imagen temporal SOLO en el producto correcto
  setImagePreview((prev) => ({
    ...prev,
    [selectedProductId]: URL.createObjectURL(file),
  }));

  await uploadImage(selectedProductId, file); // ✅ Correcto
};

const toggleVisibility = async (productId, visible) => {
    setLoading(true);
    try {
        const cliente_id = localStorage.getItem("cliente_id");
        if (!cliente_id) throw new Error("No se pudo obtener el cliente_id.");

        const response = await fetch(
            `${API_BASE_URL}/clover/products/${productId}/visibility`,
            {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ visible, cliente_id }),
            }
        );

        if (!response.ok) {
            throw new Error(`Error al cambiar visibilidad: ${response.status}`);
        }

        setProducts((prev) =>
            prev.map((product) =>
                product.id === productId ? { ...product, visible } : product
            )
        );
    } catch (err) {
        console.error("Error al cambiar visibilidad:", err);
        setError("Error al cambiar visibilidad.");
    } finally {
        setLoading(false);
    }
};

const uploadImage = async (productId, imageFile) => {
  if (!productId) {
    console.error("❌ Error: productId no está definido.");
    return;
  }

  const cliente_id = localStorage.getItem("cliente_id");
  if (!cliente_id) {
    console.error("❌ Error: cliente_id no está definido.");
    return;
  }

  console.log("📸 Subiendo imagen para producto:", productId);
  
  const formData = new FormData();
  formData.append("image", imageFile);
  formData.append("cliente_id", cliente_id);

  try {
    setUploadingImage(true);

    const response = await fetch(
      `${API_BASE_URL}/clover/products/${productId}/image`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Error al subir imagen: ${response.status}`);
    }

    const updatedProduct = await response.json();

    console.log("✅ Respuesta API:", updatedProduct);

    if (!updatedProduct.url) {
      console.warn("⚠️ La API no devolvió una URL de imagen válida.");
      return;
    }

    // 🔥 Asegurarse de actualizar SOLO el producto correcto
    setProducts((prevProducts) => 
      prevProducts.map((product) =>
        product.id === productId 
          ? { ...product, image_path: updatedProduct.url }
          : product
      )
    );

    setFilteredProducts((prevFiltered) => 
      prevFiltered.map((product) =>
        product.id === productId 
          ? { ...product, image_path: updatedProduct.url }
          : product
      )
    );

    setMessage("Imagen subida correctamente.");
  } catch (err) {
    console.error("❌ Error al subir imagen:", err);
    setError("Error al subir imagen.");
  } finally {
    setUploadingImage(false);
  }
};

const handleSaveImage = async (productId, imagePreviewUrl) => {
  const file = await fetch(imagePreviewUrl).then((res) => res.blob());
  const imageFile = new File([file], "image.jpg", { type: "image/jpeg" });

  await uploadImage(productId, imageFile);
  setImagePreview((prev) => ({ ...prev, [productId]: null })); // Ocultar vista previa después de guardar
};


  const filterProducts = () => {
    setFilteredProducts(
      products.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  return (
<div className="min-h-screen flex flex-col items-center p-4">
  {loading && <Spinner size="lg" />}
  {uploadingImage && <p className="text-blue-600">Subiendo imagen...</p>}
  {error && <p className="text-red-500">{error}</p>}
  {message && <p className="text-green-500">{message}</p>}

      <input
        type="text"
        placeholder="Buscar productos..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full p-3 rounded-lg border border-gray-300 shadow-sm focus:ring"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white border rounded-lg shadow-md p-4 flex flex-col items-center"
            >

<img
  src={imagePreview[product.id] || product.image_path}
  alt={product.name || "Producto sin nombre"}
  className="w-40 h-40 object-cover rounded-lg mb-4"
/>

{/* 🔥 Mostrar el botón "Guardar Imagen" SOLO si hay una imagen nueva */}
{imagePreview[product.id] && (
  <Button
    onClick={() => handleSaveImage(product.id, imagePreview[product.id])}
    color="success"
  >
    Guardar Imagen
  </Button>
)}



      {/* Nombre del Producto */}
      <h3 className="text-lg font-semibold">{product.name || "Sin nombre"}</h3>

      {/* Precio del Producto */}
      <p className="text-gray-600">Precio: ${product.price || "N/A"}</p>

      {/* Estado de Visibilidad */}
      <p className="text-gray-500">Visible: {product.visible ? "Sí" : "No"}</p>

      {/* 🔥 CAMPO DE DESCRIPCIÓN (EDITABLE) */}
      {editingProductId === product.id ? (
        <div className="w-full">
          <textarea
            className="w-full p-2 border rounded"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
          <div className="flex justify-between mt-2">
            <Button onClick={() => updateDescription(product.id)} color="success">
              Guardar
            </Button>
            <Button onClick={() => setEditingProductId(null)} color="failure">
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm italic mt-2">
          {product.description || "Sin descripción"}
        </p>
      )}

      {/* Botón para Editar/Agregar Descripción */}
      <Button onClick={() => setEditingProductId(product.id)} className="mt-2">
        {product.description ? "Editar Descripción" : "Agregar Descripción"}
      </Button>

             {/* Botón de Visibilidad y Subida de Imagen */}
              <div className="flex justify-around w-full mt-4">
                <Button
                  color={product.visible ? "failure" : "success"}
                  onClick={() => toggleVisibility(product.id, !product.visible)}
                >
                  {product.visible ? "Ocultar" : "Mostrar"}
                </Button>

<label
  htmlFor={`upload-${product.id}`}
  className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-lg"
>
  Cambiar Imagen
</label>

<input
  type="file"
  id={`upload-${product.id}`}
  className="hidden"
  accept="image/*"
  onChange={(e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const imageUrl = URL.createObjectURL(file);

      setImagePreview((prev) => ({ ...prev, [product.id]: imageUrl }));
      setSelectedProductId(product.id);
      setSelectedImage(imageUrl); // ✅ Asegurar que `ImageCropper` reciba la imagen correcta
      setCropModalOpen(true);
    }
  }}
/>

              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500">No se encontraron productos.</p>
        )}
      </div>

      {cropModalOpen && (
        <ImageCropper
          imageSrc={selectedImage}
          onCropComplete={handleCroppedImage}
          onCancel={() => setCropModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Products;
